import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface SubscriptionInfo {
  subscribed: boolean;
  productId: string | null;
  subscriptionEnd: string | null;
}

const MEMBERSHIP_TIERS = {
  student: {
    yearly: {
      productId: "prod_TRt64Ozk8YLPX1",
      priceId: "price_1SUzK0ILQ5pKa4NlaySrH6IW",
      name: "Student & Educator",
      price: "$44/year",
      priceAmount: 44
    },
    monthly: {
      productId: "prod_TRt5jNzLDNWk5V",
      priceId: "price_1SUzJmILQ5pKa4Nl2aseOFJP",
      name: "Student & Educator",
      price: "$4.40/month",
      priceAmount: 4.40
    }
  },
  paid: {
    yearly: {
      productId: "prod_TRt73IWUbQHS47",
      priceId: "price_1SUzLHILQ5pKa4Nl3DFXDkyy",
      name: "Paid Member",
      price: "$88/year",
      priceAmount: 88
    },
    monthly: {
      productId: "prod_TRt6PEQYFsQhsx",
      priceId: "price_1SUzKEILQ5pKa4NlfhepA3ND",
      name: "Paid Member",
      price: "$8.80/month",
      priceAmount: 8.80
    }
  }
};

export function useSubscription() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionInfo>({
    subscribed: false,
    productId: null,
    subscriptionEnd: null
  });
  const [isLoading, setIsLoading] = useState(true);

  const checkSubscription = async () => {
    if (!user) {
      setSubscription({ subscribed: false, productId: null, subscriptionEnd: null });
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("check-subscription");

      if (error) throw error;

      setSubscription(data);
    } catch (error) {
      console.error("Error checking subscription:", error);
      setSubscription({ subscribed: false, productId: null, subscriptionEnd: null });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkSubscription();
  }, [user]);

  const getTierInfo = (billingPeriod: 'monthly' | 'yearly' = 'yearly') => {
    if (!subscription.productId) return null;
    
    // Check if product matches student tier (yearly or monthly)
    if (subscription.productId === MEMBERSHIP_TIERS.student.yearly.productId || 
        subscription.productId === MEMBERSHIP_TIERS.student.monthly.productId) {
      return MEMBERSHIP_TIERS.student[billingPeriod];
    } 
    // Check if product matches paid tier (yearly or monthly)
    else if (subscription.productId === MEMBERSHIP_TIERS.paid.yearly.productId ||
             subscription.productId === MEMBERSHIP_TIERS.paid.monthly.productId) {
      return MEMBERSHIP_TIERS.paid[billingPeriod];
    }
    return null;
  };

  return {
    subscription,
    isLoading,
    checkSubscription,
    getTierInfo,
    MEMBERSHIP_TIERS
  };
}
